import React from 'react';
import { HardDrive, Layers, Network, Server, Database, Cloud, Shield, Route, KeyRound, AppWindow, Network as LoadBalancer } from 'lucide-react';

const NODE_SIZE = { width: 220, height: 120 };

const PALETTE_ITEMS = [
  {
    type: 'resource-group',
    label: 'Resource Group',
    description: 'Azure container for related resources',
    icon: Server,
    iconColor: 'text-teal-600 dark:text-teal-400',
    defaultData: { type: 'resource-group', name: 'rg-landing-zone', region: 'eastus' },
    defaultStyle: NODE_SIZE,
  },
  {
    type: 'vnet',
    label: 'Virtual Network',
    description: 'Isolated network boundary',
    icon: Network,
    iconColor: 'text-blue-600 dark:text-blue-400',
    defaultData: { type: 'vnet', name: 'vnet-hub', cidr: '10.0.0.0/16', region: 'eastus' },
    defaultStyle: NODE_SIZE,
  },
  {
    type: 'subnet',
    label: 'Subnet',
    description: 'Network segment within a VNet',
    icon: Layers,
    iconColor: 'text-teal-600 dark:text-teal-400',
    defaultData: { type: 'subnet', name: 'snet-default', cidr: '10.0.1.0/24' },
    defaultStyle: NODE_SIZE,
  },
  {
    type: 'virtual-machine',
    label: 'Virtual Machine',
    description: 'Linux VM with configurable SKU',
    icon: Cloud,
    iconColor: 'text-purple-600 dark:text-purple-400',
    defaultData: { type: 'virtual-machine', name: 'vm-app', sku: 'Standard_B2s' },
    defaultStyle: NODE_SIZE,
  },
  {
    type: 'managed-disk',
    label: 'Managed Disk',
    description: 'Premium or Standard managed disk',
    icon: HardDrive,
    iconColor: 'text-amber-600 dark:text-amber-400',
    defaultData: { type: 'managed-disk', name: 'disk-data', diskType: 'Premium_LRS', diskSize: 128 },
    defaultStyle: NODE_SIZE,
  },
  {
    type: 'storage-account',
    label: 'Storage Account',
    description: 'Blob, file, table, and queue storage',
    icon: Database,
    iconColor: 'text-orange-600 dark:text-orange-400',
    defaultData: { type: 'storage-account', name: 'stgdata', accountTier: 'Standard', replicationType: 'LRS' },
    defaultStyle: NODE_SIZE,
  },
  {
    type: 'nsg',
    label: 'Network Security Group',
    description: 'Filter network traffic with security rules',
    icon: Shield,
    iconColor: 'text-red-600 dark:text-red-400',
    defaultData: { type: 'nsg', name: 'nsg-app', securityRules: 'Allow-SSH|100|Inbound|Allow|*|*|*|*|22|TCP' },
    defaultStyle: NODE_SIZE,
  },
  {
    type: 'route-table',
    label: 'Route Table',
    description: 'Custom routing for subnet traffic',
    icon: Route,
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    defaultData: { type: 'route-table', name: 'rt-app', routes: 'to-internet|0.0.0.0/0|Internet' },
    defaultStyle: NODE_SIZE,
  },
  {
    type: 'key-vault',
    label: 'Key Vault',
    description: 'Secure key, secret, and certificate storage',
    icon: KeyRound,
    iconColor: 'text-green-600 dark:text-green-400',
    defaultData: { type: 'key-vault', name: 'kv-secrets', kvSku: 'standard' },
    defaultStyle: NODE_SIZE,
  },
  {
    type: 'app-service',
    label: 'App Service',
    description: 'Web app hosting (Linux/Windows)',
    icon: AppWindow,
    iconColor: 'text-sky-600 dark:text-sky-400',
    defaultData: { type: 'app-service', name: 'app-web', appOsType: 'Linux', appRuntime: 'NODE:18-lts' },
    defaultStyle: NODE_SIZE,
  },
  {
    type: 'load-balancer',
    label: 'Load Balancer',
    description: 'Distribute traffic across VMs',
    icon: LoadBalancer,
    iconColor: 'text-cyan-600 dark:text-cyan-400',
    defaultData: { type: 'load-balancer', name: 'lb-app', lbSku: 'Standard', lbType: 'Public' },
    defaultStyle: NODE_SIZE,
  },
];

function handleDragStart(e, item) {
  e.dataTransfer.setData(
    'application/reactflow',
    JSON.stringify({ type: item.type, data: item.defaultData, style: item.defaultStyle })
  );
  e.dataTransfer.effectAllowed = 'move';
}

const Palette = () => {
  return (
    <div className="flex flex-col gap-2 p-3">
      <div className="mb-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Components
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">Drag onto the canvas</p>
      </div>
      {PALETTE_ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <div
            key={item.type}
            draggable
            onDragStart={(e) => handleDragStart(e, item)}
            className="cursor-grab select-none border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm transition-all duration-150 hover:shadow-md hover:-translate-y-0.5 active:cursor-grabbing active:shadow-sm active:translate-y-0"
          >
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 rounded-md bg-gray-100 dark:bg-gray-700 p-1.5">
                <Icon className={`h-4 w-4 ${item.iconColor}`} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100">
                  {item.label}
                </p>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400 leading-snug">
                  {item.description}
                </p>
              </div>
            </div>
            {item.defaultData.cidr && (
              <span className="mt-2 inline-block font-mono text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                {item.defaultData.cidr}
              </span>
            )}
            {item.defaultData.sku && (
              <span className="mt-2 inline-block font-mono text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                {item.defaultData.sku}
              </span>
            )}
            {item.defaultData.diskType && (
              <span className="mt-2 inline-block font-mono text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                {item.defaultData.diskType} &middot; {item.defaultData.diskSize} GB
              </span>
            )}
            {item.defaultData.accountTier && (
              <span className="mt-2 inline-block font-mono text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                {item.defaultData.accountTier} &middot; {item.defaultData.replicationType}
              </span>
            )}
            {item.defaultData.securityRules && (
              <span className="mt-2 inline-block font-mono text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                Security Rules
              </span>
            )}
            {item.defaultData.routes && (
              <span className="mt-2 inline-block font-mono text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded">
                Custom Routes
              </span>
            )}
          </div>
        );
      })}

      <div className="mt-4 rounded-md border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-3">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Nesting</p>
        <ul className="mt-1.5 space-y-1 text-xs text-gray-500 dark:text-gray-400">
          <li>&bull; Drop VNets inside Resource Groups</li>
          <li>&bull; Drop Subnets inside VNets</li>
          <li>&bull; Connect nodes with edges</li>
        </ul>
      </div>
    </div>
  );
};

export default Palette;
